<script setup>
/* eslint-disable no-console */
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import TicketsAPI from 'dashboard/api/tickets';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';
import ContractsAPI from 'dashboard/api/contracts';
import {
  crearOrdenTrabajo,
  consultaDetalleContratoJson,
  getContrato,
  getPuntoServicioPorContador,
  referenceWorkOrderAquacis,
  refreshDataJson,
  xmlToJson,
  extractSoapFaultString,
} from 'dashboard/api/cea';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  ticket: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'generated']);

const { t } = useI18n();
const dialogRef = ref(null);

// Mode toggle: 'create' or 'link'
const mode = ref('create');

// Form state (create mode)
const defaultOrderTypeId = ref('');
const selectedOrderTypeId = ref('');
const selectedOrderMotiveId = ref('');
const selectedRepairCodeId = ref('');
const requestDate = ref(new Date().toISOString().split('T')[0]);
const contractNumber = ref('');
const contractValidationStatus = ref('idle');
const contractOwnerLabel = ref('');
let contractValidateTimer = null;
const CONTRACT_VALIDATE_DEBOUNCE_MS = 500;

const observations = ref('');
const description = ref('');
const isSubmitting = ref(false);
const selectedOrderClass = ref('work_order');
const detectedOrderClass = ref('work_order');

// Aquasis manual override state
const aquasisLookupError = ref('');
const manualIdPtoServicio = ref('');
const showManualFields = ref(false);

// Lookup data
const orderTypes = ref([]);
const orderMotives = ref([]);
const subcategoryRepairCodes = ref([]);
const isLoadingLookups = ref(false);
const isLoadingDefaults = ref(false);

// Constraint state from subcategory defaults
const allowedOrderMotives = ref([]);
const hasOtConstraint = ref(false);
const hasOmConstraint = ref(false);
const isApplyingSubcategoryDefaults = ref(false);
/** Set from GET …/defaults so we know the subcategory even if ticket.ticket_subcategory is missing. */
const defaultsSubcategoryCode = ref('');

// Link mode state
const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const selectedOrderId = ref(null);
let searchDebounceTimer = null;

/** 'idle' | 'loading' | 'found' | 'not-found' | 'error' | 'needs-explotacion' */
const aquasisLookupStatus = ref('idle');

/** operationalSiteID from last successful refreshData (for aquasis_exploitation_code on submit). */
const lastAquasisRefreshOperationalSite = ref('');

/** True when the search query looks like an Aquasis order id (e.g. O4624437). */
const queryLooksLikeAquasisId = computed(() =>
  /^[Oo]\d{4,}/i.test(searchQuery.value?.trim() || '')
);

/**
 * True when the query matches the Aquasis pattern, the search finished, and
 * no AGORA order was found — meaning the order likely exists only in Aquasis.
 */
const showExternalAquasisForm = computed(
  () =>
    queryLooksLikeAquasisId.value &&
    !isSearching.value &&
    searchResults.value.length === 0 &&
    (searchQuery.value?.trim() || '').length >= 2
);

const OBSERVATIONS_ALPHANUMERIC_REGEX =
  /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ.,;:\-¿?¡!()"']*$/;
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
const sanitizeObservations = val =>
  (val || '').replace(/[^a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ.,;:\-¿?¡!()"']/g, '');
const handleObservationsInput = e => {
  observations.value = sanitizeObservations(e.target.value).slice(0, 80);
};

const ORDER_TYPE_MOTIVE_MAP = {
  32: ['52'],
  40: ['94'],
  33: ['70', '41'],
  6: ['61', '50'],
  23: ['66', '15'],
  21: ['86', '96', '89', '80'],
};

const resolvedSubcategoryCode = computed(() => {
  const fromDefaults = defaultsSubcategoryCode.value?.trim();
  if (fromDefaults) return fromDefaults;
  return (props.ticket.ticket_subcategory?.code || '').trim();
});

const effectiveOrderMotives = computed(() => {
  if (resolvedSubcategoryCode.value === 'REP-BAP') {
    return orderMotives.value.filter(om => String(om.code) === '86');
  }

  const selectedOt = orderTypes.value.find(
    ot => String(ot.id) === String(selectedOrderTypeId.value)
  );
  const motiveCodes =
    selectedOt && ORDER_TYPE_MOTIVE_MAP[String(selectedOt.code)];
  const hasMotiveMap = Array.isArray(motiveCodes) && motiveCodes.length > 0;
  const codesSet = hasMotiveMap
    ? new Set(motiveCodes.map(String))
    : null;
  const mapFiltered = hasMotiveMap
    ? orderMotives.value.filter(om => codesSet.has(String(om.code)))
    : null;

  const constrainedBySubcategory =
    hasOmConstraint.value && allowedOrderMotives.value.length > 0;

  if (constrainedBySubcategory && hasMotiveMap) {
    const intersected = allowedOrderMotives.value.filter(om =>
      codesSet.has(String(om.code))
    );
    return intersected.length > 0 ? intersected : mapFiltered;
  }

  if (constrainedBySubcategory) {
    return allowedOrderMotives.value;
  }

  if (mapFiltered?.length) return mapFiltered;
  return orderMotives.value;
});

const effectiveOrderTypes = computed(() => {
  const list = orderTypes.value;
  if (resolvedSubcategoryCode.value === 'REP-BAP') {
    return list.filter(ot => String(ot.code) === '21');
  }
  return list;
});

const syncOrderTypeSelectionToAllowedList = () => {
  const allowed = effectiveOrderTypes.value;
  if (!allowed.length) return;
  const cur = String(selectedOrderTypeId.value);
  if (!allowed.some(ot => String(ot.id) === cur)) {
    selectedOrderTypeId.value = allowed[0].id;
  }
};

const syncOrderMotiveSelectionToAllowedList = () => {
  const allowed = effectiveOrderMotives.value;
  if (!allowed.length) return;
  const cur = String(selectedOrderMotiveId.value);
  if (!allowed.some(om => String(om.id) === cur)) {
    selectedOrderMotiveId.value = allowed[0].id;
  }
};

const isOrderTypeLocked = computed(
  () => resolvedSubcategoryCode.value === 'REP-BAP'
);
const isOrderMotiveLocked = computed(
  () => resolvedSubcategoryCode.value === 'REP-BAP'
);

const dialogTitle = computed(() => {
  if (mode.value === 'link') {
    return t('TICKETS.GENERATE_ORDER.MODE_LINK');
  }
  return selectedOrderClass.value === 'service_order'
    ? 'Generar Orden de Servicio'
    : 'Generar Orden de Trabajo';
});

const confirmButtonLabel = computed(() => {
  if (isSubmitting.value) {
    return mode.value === 'link' ? 'Vinculando...' : 'Generando...';
  }
  return mode.value === 'link' ? 'Vincular Orden' : 'Generar Orden';
});

const selectedOrderType = computed(() =>
  orderTypes.value.find(
    ot => String(ot.id) === String(selectedOrderTypeId.value)
  )
);
const isRepairCodeRequired = computed(
  () =>
    subcategoryRepairCodes.value.length > 0 &&
    selectedOrderType.value?.code === '32'
);

const isContractOkForSubmit = computed(() => {
  const trimmed = contractNumber.value?.trim() || '';
  if (!trimmed || !/^\d+$/.test(trimmed)) return false;
  return contractValidationStatus.value === 'valid';
});

const contractInputClass = computed(() => {
  const base =
    'w-full px-3 py-2 pr-10 rounded-lg bg-n-solid-3 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2';
  if (contractValidationStatus.value === 'valid') {
    return `${base} border border-n-teal-9 focus:ring-n-teal-9`;
  }
  if (
    contractValidationStatus.value === 'invalid' ||
    contractValidationStatus.value === 'error'
  ) {
    return `${base} border border-n-ruby-9 focus:ring-n-ruby-9`;
  }
  return `${base} border border-n-weak focus:ring-n-brand`;
});

const contractInvalidMessage = computed(() => {
  const trimmed = contractNumber.value?.trim() || '';
  if (trimmed && !/^\d+$/.test(trimmed)) {
    return t('TICKETS.GENERATE_ORDER.CONTRACT_DIGITS_ONLY');
  }
  return t('TICKETS.GENERATE_ORDER.CONTRACT_NOT_FOUND');
});

const showContractValidationAffix = computed(() =>
  ['checking', 'valid', 'invalid', 'error'].includes(
    contractValidationStatus.value
  )
);

const parseContractFromDetail = detail => {
  const contractData = detail?.GenericoContratoDTO || detail;
  if (!contractData?.contrato) return null;
  const contract = contractData.contrato;
  const datosPersonales = contractData.datosPersonales || {};
  return {
    numeroContrato: contract.numeroContrato,
    titular: contract.titular || datosPersonales.titular || '',
  };
};

/** Explotación / operational site code from SOAP contract detail (same shape as create flow). */
const extractExplotacionFromContratoDetail = detail => {
  const contrato =
    detail?.GenericoContratoDTO?.contrato || detail?.contrato || {};
  return String(contrato.explotacion || '').trim();
};

/**
 * Aquasis refreshData requires operationalSiteID (e.g. 12 → 0012). Empty → upstream 500.
 * Resolve from ticket, localStorage (contrato list), or contract SOAP.
 */
const resolveOperationalSiteIdForRefreshData = async () => {
  const t = props.ticket || {};
  const fromTicket =
    String(t.explotacion || '').trim() ||
    String(t.custom_attributes?.explotacion || '').trim() ||
    String(t.metadata?.explotacion || '').trim() ||
    '';
  if (fromTicket) return fromTicket;

  const num = String(t.contract_number || '').trim();
  if (num && typeof localStorage !== 'undefined') {
    const ls = localStorage.getItem(`contrato_${num}_explotacion`);
    if (ls && String(ls).trim()) return String(ls).trim();
  }
  if (num) {
    try {
      let detail = await consultaDetalleContratoJson(num, 'es');
      let ex = extractExplotacionFromContratoDetail(detail);
      if (!ex) {
        const resolveRes = await ContractsAPI.resolveNumber(num);
        const cnttnum = resolveRes?.data?.number;
        if (cnttnum) {
          detail = await consultaDetalleContratoJson(String(cnttnum), 'es');
          ex = extractExplotacionFromContratoDetail(detail);
        }
      }
      if (ex) return ex;
    } catch (e) {
      console.warn('resolveOperationalSiteIdForRefreshData: contract detail failed', e);
    }
  }
  return '';
};

const runContractValidation = async () => {
  const trimmed = contractNumber.value?.trim() || '';
  if (!trimmed) {
    contractValidationStatus.value = 'idle';
    contractOwnerLabel.value = '';
    return;
  }
  if (!/^\d+$/.test(trimmed)) {
    contractValidationStatus.value = 'invalid';
    contractOwnerLabel.value = '';
    return;
  }

  contractValidationStatus.value = 'checking';
  contractOwnerLabel.value = '';
  const requestKey = trimmed;

  try {
    let detail = await consultaDetalleContratoJson(trimmed, 'es');
    let contract = parseContractFromDetail(detail);

    if (!contract) {
      const resolveRes = await ContractsAPI.resolveNumber(trimmed);
      const cnttnum = resolveRes?.data?.number;
      if (cnttnum) {
        detail = await consultaDetalleContratoJson(String(cnttnum), 'es');
        contract = parseContractFromDetail(detail);
      }
    }

    if (contractNumber.value?.trim() !== requestKey) return;

    if (contract?.numeroContrato) {
      contractValidationStatus.value = 'valid';
      contractOwnerLabel.value = (contract.titular || '').trim();
    } else {
      contractValidationStatus.value = 'invalid';
    }
  } catch (error) {
    console.error('Contract validation error:', error);
    if (contractNumber.value?.trim() !== requestKey) return;
    contractValidationStatus.value = 'error';
  }
};

const scheduleContractValidation = () => {
  if (contractValidateTimer) clearTimeout(contractValidateTimer);
  const trimmed = contractNumber.value?.trim() || '';
  if (!trimmed) {
    contractValidationStatus.value = 'idle';
    contractOwnerLabel.value = '';
    return;
  }
  if (!/^\d+$/.test(trimmed)) {
    contractValidationStatus.value = 'invalid';
    contractOwnerLabel.value = '';
    return;
  }
  contractValidationStatus.value = 'idle';
  contractValidateTimer = setTimeout(() => {
    void runContractValidation();
  }, CONTRACT_VALIDATE_DEBOUNCE_MS);
};

const isFormValid = computed(() => {
  if (mode.value === 'link') {
    if (selectedOrderId.value) return true;
    if (showExternalAquasisForm.value) {
      if (
        !selectedOrderTypeId.value ||
        !selectedOrderMotiveId.value ||
        !requestDate.value
      ) {
        return false;
      }
      if (isRepairCodeRequired.value && !selectedRepairCodeId.value) {
        return false;
      }
      const obs = observations.value?.trim() || '';
      return (
        obs.length > 0 &&
        obs.length <= 100 &&
        OBSERVATIONS_ALPHANUMERIC_REGEX.test(obs)
      );
    }
    return false;
  }
  if (!isContractOkForSubmit.value) return false;
  const baseValid =
    selectedOrderTypeId.value &&
    selectedOrderMotiveId.value &&
    requestDate.value;
  if (!baseValid) return false;
  if (isRepairCodeRequired.value && !selectedRepairCodeId.value) return false;
  const obs = observations.value?.trim() || '';
  return (
    obs.length > 0 &&
    obs.length <= 100 &&
    OBSERVATIONS_ALPHANUMERIC_REGEX.test(obs)
  );
});

const fetchLookups = async () => {
  try {
    isLoadingLookups.value = true;
    const [typesRes, motivesRes] = await Promise.all([
      CeaLookupsAPI.get('order_types'),
      CeaLookupsAPI.get('order_motives'),
    ]);
    orderTypes.value = typesRes.data;
    orderMotives.value = motivesRes.data;
  } catch (error) {
    console.error('Error fetching CEA lookups:', error);
    orderTypes.value = [];
    orderMotives.value = [];
  } finally {
    isLoadingLookups.value = false;
  }
};

const fetchSubcategoryDefaults = async () => {
  const categoryId = props.ticket.ticket_category_id;
  const subcategoryId = props.ticket.ticket_subcategory_id;
  if (!categoryId || !subcategoryId) return;

  isApplyingSubcategoryDefaults.value = true;
  defaultsSubcategoryCode.value = '';
  try {
    isLoadingDefaults.value = true;
    const response = await TicketCategoriesAPI.getSubcategoryDefaults(
      categoryId,
      subcategoryId
    );
    const defaults = response.data;

    defaultsSubcategoryCode.value = (
      defaults.subcategory_code ||
      props.ticket.ticket_subcategory?.code ||
      ''
    ).trim();

    defaultOrderTypeId.value = '';
    if (defaults.default_order_type) {
      defaultOrderTypeId.value = defaults.default_order_type.id;
      selectedOrderTypeId.value = defaults.default_order_type.id;
      hasOtConstraint.value = true;
    }

    if (defaults.order_motives?.length > 0) {
      allowedOrderMotives.value = defaults.order_motives;
      hasOmConstraint.value = true;
      if (defaults.order_motives.length === 1) {
        selectedOrderMotiveId.value = defaults.order_motives[0].id;
      }
    }

    subcategoryRepairCodes.value = defaults.repair_codes || [];
    if (subcategoryRepairCodes.value.length > 0) {
      selectedRepairCodeId.value = subcategoryRepairCodes.value[0].id;
    }
  } catch (error) {
    console.error('Error fetching subcategory defaults:', error);
  } finally {
    isLoadingDefaults.value = false;
  }
  syncOrderTypeSelectionToAllowedList();
  syncOrderMotiveSelectionToAllowedList();
  await nextTick();
  isApplyingSubcategoryDefaults.value = false;
};

const formatOrderTypeString = () => {
  const ot = orderTypes.value.find(
    item => String(item.id) === String(selectedOrderTypeId.value)
  );
  return ot ? `${ot.code} - ${ot.description}` : '';
};

const formatRepairCodeString = () => {
  const rc = subcategoryRepairCodes.value.find(
    r => r.id === selectedRepairCodeId.value
  );
  return rc ? `${rc.code} - ${rc.description}` : '';
};

const handleClose = () => {
  mode.value = 'create';
  selectedOrderTypeId.value = '';
  selectedOrderMotiveId.value = '';
  selectedRepairCodeId.value = '';
  contractNumber.value = '';
  requestDate.value = new Date().toISOString().split('T')[0];
  observations.value = '';
  description.value = '';
  subcategoryRepairCodes.value = [];
  allowedOrderMotives.value = [];
  defaultOrderTypeId.value = '';
  hasOtConstraint.value = false;
  hasOmConstraint.value = false;
  isApplyingSubcategoryDefaults.value = false;
  defaultsSubcategoryCode.value = '';
  selectedOrderClass.value = 'work_order';
  detectedOrderClass.value = 'work_order';
  aquasisLookupError.value = '';
  manualIdPtoServicio.value = '';
  showManualFields.value = false;
  if (contractValidateTimer) clearTimeout(contractValidateTimer);
  contractValidateTimer = null;
  contractValidationStatus.value = 'idle';
  contractOwnerLabel.value = '';
  searchQuery.value = '';
  searchResults.value = [];
  selectedOrderId.value = null;
  aquasisLookupStatus.value = 'idle';
  lastAquasisRefreshOperationalSite.value = '';
  emit('close');
};

// --- Link mode search ---

/**
 * Pre-fill form fields from a refreshDataJson response.
 * Mapping: otData.otClassID → order_class, otData.otTypeID → order type,
 *          otData.requestDate → requestDate, otData.originMotiveDescription → description.
 */
const prefillFromAquasisOrder = async otData => {
  if (!otData) return;

  const site = String(otData.operationalSiteID || '').trim();
  if (site) {
    lastAquasisRefreshOperationalSite.value = site.replace(/^0+/, '') || site;
  }

  // order class: 1 = Orden de Trabajo, 2 = Orden de Servicio (Aquasis convention)
  const classId = parseInt(otData.otClassID ?? otData.otclass ?? -1, 10);
  if (classId === 2) {
    selectedOrderClass.value = 'service_order';
  } else if (classId === 1 || classId === 0) {
    selectedOrderClass.value = 'work_order';
  }

  // order type: match by code
  if (otData.otTypeID) {
    const match = orderTypes.value.find(
      ot => String(ot.code) === String(otData.otTypeID)
    );
    if (match) {
      selectedOrderTypeId.value = String(match.id);
    }
  }

  // request date: ISO string → YYYY-MM-DD
  if (otData.requestDate) {
    const raw = String(otData.requestDate);
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw.slice(0, 10);
    if (datePart && datePart.length === 10) requestDate.value = datePart;
  }

  // description from motive text
  if (otData.originMotiveDescription) {
    description.value = String(otData.originMotiveDescription);
  }

  // After type is set, auto-select motive if exactly one option is available
  await nextTick();
  if (
    !selectedOrderMotiveId.value &&
    effectiveOrderMotives.value.length === 1
  ) {
    selectedOrderMotiveId.value = String(effectiveOrderMotives.value[0].id);
  }
};

/**
 * Query Aquasis for a single order by its ID. On success, pre-fills the form.
 * Sets aquasisLookupStatus accordingly.
 */
const lookupAquasisOrder = async orderId => {
  aquasisLookupStatus.value = 'loading';
  lastAquasisRefreshOperationalSite.value = '';
  try {
    const operationalSiteId = await resolveOperationalSiteIdForRefreshData();
    if (!operationalSiteId) {
      aquasisLookupStatus.value = 'needs-explotacion';
      return;
    }

    const json = await refreshDataJson(orderId, operationalSiteId);
    // refreshDataJson wraps the return element; the OT data lives under otData
    const otData =
      json?.otData ||
      json?.return?.otData ||
      json?.refreshDataReturn?.otData ||
      null;

    if (!otData) {
      aquasisLookupStatus.value = 'not-found';
      return;
    }

    await prefillFromAquasisOrder(otData);
    aquasisLookupStatus.value = 'found';
  } catch (err) {
    console.error('refreshDataJson lookup failed:', err);
    aquasisLookupStatus.value = 'error';
  }
};

const searchOrders = async () => {
  const query = searchQuery.value?.trim();
  if (!query || query.length < 2) {
    searchResults.value = [];
    aquasisLookupStatus.value = 'idle';
    return;
  }

  try {
    isSearching.value = true;
    aquasisLookupStatus.value = 'idle';
    const response = await ServiceOrdersAPI.get({ search: query });
    searchResults.value = response.data.payload || [];

    // When no AGORA match and query looks like an Aquasis order id, query Aquasis
    if (searchResults.value.length === 0 && /^[Oo]\d{4,}/i.test(query)) {
      await lookupAquasisOrder(query);
    }
  } catch (error) {
    console.error('Error searching orders:', error);
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const onSearchInput = () => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(searchOrders, 300);
};

const selectOrder = orderId => {
  selectedOrderId.value = selectedOrderId.value === orderId ? null : orderId;
};

const buildLocalServiceOrderPayload = (aquasisOrderId, exploitationCode) => {
  const orderData = {
    order_class: selectedOrderClass.value,
    order_type: formatOrderTypeString(),
    order_type_record_id: selectedOrderTypeId.value,
    order_motive_id: selectedOrderMotiveId.value,
    request_date: requestDate.value,
    observations: observations.value,
    reason: description.value,
  };

  if (selectedRepairCodeId.value && selectedOrderType.value?.code === '32') {
    orderData.repair_code = formatRepairCodeString();
    orderData.repair_code_record_id = selectedRepairCodeId.value;
  }

  if (aquasisOrderId) {
    orderData.aquasis_order_id = aquasisOrderId;
  }
  if (exploitationCode) {
    orderData.aquasis_exploitation_code = exploitationCode;
  }
  return orderData;
};

const getOrderStatusClass = status => {
  const classes = {
    pending: 'bg-n-amber-9/10 text-n-amber-11',
    in_progress: 'bg-n-brand/10 text-n-blue-text',
    completed: 'bg-n-teal-9/10 text-n-teal-11',
    cancelled: 'bg-n-ruby-9/10 text-n-ruby-11',
  };
  return classes[status] || 'bg-n-slate-9/10 text-n-slate-11';
};

// --- Link submit: attach ticket to an existing AGORA service order ---
const handleLinkTicketSubmit = async () => {
  if (!selectedOrderId.value || isSubmitting.value) return;

  try {
    isSubmitting.value = true;
    await ServiceOrdersAPI.linkTicket(selectedOrderId.value, props.ticket.id);
    useAlert(t('TICKETS.GENERATE_ORDER.LINK_SUCCESS'));
    emit('generated', { linked: true, order_id: selectedOrderId.value });
    handleClose();
  } catch (error) {
    console.error('Error linking ticket to order:', error);
    useAlert('Error al vincular el ticket con la orden');
  } finally {
    isSubmitting.value = false;
  }
};

// --- Link submit: Aquasis order exists but no AGORA row yet ---
const handleLinkExternalAquasisSubmit = async () => {
  const aquasisId = searchQuery.value?.trim();
  if (!aquasisId || isSubmitting.value) return;

  const obs = observations.value?.trim() || '';
  if (obs.length === 0) {
    useAlert(t('ORDERS.DETAIL.OBSERVATIONS_REQUIRED'));
    return;
  }
  if (obs.length > 100) {
    useAlert(t('ORDERS.DETAIL.OBSERVATIONS_MAX_LENGTH'));
    return;
  }
  if (!OBSERVATIONS_ALPHANUMERIC_REGEX.test(obs)) {
    useAlert(t('ORDERS.DETAIL.OBSERVATIONS_ALPHANUMERIC'));
    return;
  }

  try {
    isSubmitting.value = true;
    const exploitation =
      lastAquasisRefreshOperationalSite.value?.trim() ||
      props.ticket.explotacion?.trim() ||
      '';
    const orderData = buildLocalServiceOrderPayload(aquasisId, exploitation);
    const response = await TicketsAPI.createServiceOrder(
      props.ticket.display_id,
      orderData
    );

    const caseId =
      props.ticket.custom_attributes?.caso_sn ||
      props.ticket.custom_attributes?.sys_id ||
      props.ticket.metadata?.caso_sn ||
      props.ticket.metadata?.sys_id;

    if (caseId) {
      try {
        await referenceWorkOrderAquacis(caseId, aquasisId);
      } catch (refErr) {
        console.error('Error referencing Aquasis order on CEA case:', refErr);
        useAlert(t('TICKETS.GENERATE_ORDER.LINK_EXTERNAL_CEA_WARNING'));
      }
    } else {
      useAlert(t('TICKETS.GENERATE_ORDER.LINK_EXTERNAL_NO_CASE_ID'));
    }

    useAlert(t('TICKETS.GENERATE_ORDER.LINK_EXTERNAL_SUCCESS', { id: aquasisId }));
    emit('generated', response.data);
    handleClose();
  } catch (error) {
    console.error('Error creating service order from external Aquasis id:', error);
    useAlert(t('TICKETS.GENERATE_ORDER.LINK_EXTERNAL_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

// --- Create mode (existing logic) ---
const createAquasisOrder = async () => {
  const contractNum = contractNumber.value?.trim();
  if (!contractNum) return null;

  // Step 1: Fetch contract detail to get meter serial and exploitation code
  let idPtoServicio = '';
  let exploitationCode = '';
  let contractDetail;
  try {
    contractDetail = await consultaDetalleContratoJson(contractNum);
  } catch (error) {
    console.error('Error fetching contract detail:', error);
    aquasisLookupError.value = 'No se pudo consultar el contrato en Aquasis';
    throw error;
  }

  let contrato =
    contractDetail?.GenericoContratoDTO?.contrato ||
    contractDetail?.contrato ||
    {};
  let meterSerial = contrato.numeroContador || '';
  exploitationCode = contrato.explotacion || '';

  // Retry Step 1 once when the response is missing critical fields — the
  // Aquasis SOAP service intermittently returns incomplete data.
  if (!meterSerial) {
    await sleep(1000);
    try {
      const retryDetail = await consultaDetalleContratoJson(contractNum);
      const retryContrato =
        retryDetail?.GenericoContratoDTO?.contrato ||
        retryDetail?.contrato ||
        {};
      meterSerial = retryContrato.numeroContador || meterSerial;
      exploitationCode = retryContrato.explotacion || exploitationCode;
    } catch (retryError) {
      console.error('Error retrying contract detail fetch:', retryError);
    }
  }

  if (!meterSerial) {
    aquasisLookupError.value =
      'El contrato no tiene numero de contador asociado';
    showManualFields.value = true;
  }

  // Step 2: Look up idPtoServicio from meter serial
  if (meterSerial) {
    try {
      const meterXml = await getPuntoServicioPorContador(
        meterSerial,
        'WSGESTIONDEUDA'
      );
      const meterReturn = meterXml.getElementsByTagName(
        'getPuntoServicioPorContadorReturn'
      )[0];
      if (meterReturn) {
        const meterJson = xmlToJson(meterReturn);
        idPtoServicio =
          meterJson?.puntosServicioContadorDTO?.PuntosServicioContadorDTO
            ?.puntoServicioDTO?.PuntoServicioDTO?.id || '';
      }
    } catch (error) {
      console.error('Error fetching idPtoServicio via meter:', error);
    }
  }

  // Step 2b: Fallback — try getContrato to get idPuntoServicio
  if (!idPtoServicio) {
    try {
      const contratoXml = await getContrato(contractNum);
      const contratoReturn =
        contratoXml.getElementsByTagName('getContratoReturn')[0] ||
        contratoXml.getElementsByTagName('return')[0];
      if (contratoReturn) {
        const contratoJson = xmlToJson(contratoReturn);
        idPtoServicio = String(contratoJson?.idPuntoServicio || '');
      }
    } catch (error) {
      console.error('Error fetching idPtoServicio via getContrato:', error);
    }
  }

  // Step 2c: Show manual input if both lookups failed
  if (!idPtoServicio) {
    aquasisLookupError.value =
      'No se encontro el punto de servicio para el contrato';
    showManualFields.value = true;
  }

  // Use manual override if provided
  if (manualIdPtoServicio.value?.trim()) {
    idPtoServicio = manualIdPtoServicio.value.trim();
  }

  // Cannot proceed without idPtoServicio
  if (!idPtoServicio) {
    const err = new Error('idPtoServicio is required');
    err.isFieldError = true;
    throw err;
  }

  // Step 3: Build payload and call crearOrdenTrabajo
  const selectedOt = orderTypes.value.find(
    item => String(item.id) === String(selectedOrderTypeId.value)
  );
  const selectedOm = effectiveOrderMotives.value.find(
    item => String(item.id) === String(selectedOrderMotiveId.value)
  );
  const selectedRc =
    selectedOt?.code === '32'
      ? subcategoryRepairCodes.value.find(
          r => r.id === selectedRepairCodeId.value
        )
      : undefined;

  const payload = {
    tipoOrden: selectedOt?.code || '',
    motivoOrden: selectedOm?.code || '',
    fechaCreacionOrden: new Date(requestDate.value).toISOString(),
    numContrato: contractNum,
    idPtoServicio,
    fechaEstimdaFin: new Date(requestDate.value).toISOString(),
    observaciones: (observations.value || '').slice(0, 80),
    codigoReparacion: selectedRc?.code || '',
    anyoExpediente: String(new Date().getFullYear()),
  };

  // Step 3: Call crearOrdenTrabajo with up to 2 automatic retries on
  // transient DataException faults (4 s delay between attempts).
  let xmlDoc;
  for (let attempt = 0; ; attempt++) {
    try {
      // eslint-disable-next-line no-await-in-loop
      xmlDoc = await crearOrdenTrabajo(payload);
      break;
    } catch (err) {
      const isDataEx =
        (err.message || '').includes('DataException') ||
        (err.aquasisFault || '').includes('DataException');
      if (isDataEx && attempt < 1) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(1000);
      } else {
        throw err;
      }
    }
  }

  // Parse the response to extract the order number
  const returnEl =
    xmlDoc.getElementsByTagName('return')[0] ||
    xmlDoc.getElementsByTagName('crearOrdenTrabajoReturn')[0] ||
    xmlDoc.getElementsByTagName('crearOrdenTrabajoResponse')[0];

  if (returnEl) {
    let aquasisOrderId = null;
    // Check if it has child elements (structured response)
    if (returnEl.children && returnEl.children.length > 0) {
      const json = xmlToJson(returnEl);
      aquasisOrderId =
        json?.numOrden ||
        json?.ordenTrabajo ||
        returnEl.textContent?.trim() ||
        null;
    } else {
      // Simple text response like <return>O4514014</return>
      aquasisOrderId = returnEl.textContent?.trim() || null;
    }
    return { aquasisOrderId, exploitationCode };
  }

  return null;
};

const linkAquasisOrder = async aquasisOrderId => {
  const caseId =
    props.ticket.custom_attributes?.caso_sn ||
    props.ticket.custom_attributes?.sys_id ||
    props.ticket.metadata?.caso_sn ||
    props.ticket.metadata?.sys_id;

  if (!caseId || !aquasisOrderId) return;

  try {
    await referenceWorkOrderAquacis(caseId, aquasisOrderId);
  } catch (error) {
    console.error('Error linking Aquasis order to case:', error);
  }
};

const handleCreateSubmit = async () => {
  if (!isFormValid.value || isSubmitting.value) return;

  const obs = observations.value?.trim() || '';
  if (obs.length === 0) {
    useAlert(t('ORDERS.DETAIL.OBSERVATIONS_REQUIRED'));
    return;
  }
  if (obs.length > 100) {
    useAlert(t('ORDERS.DETAIL.OBSERVATIONS_MAX_LENGTH'));
    return;
  }
  if (!OBSERVATIONS_ALPHANUMERIC_REGEX.test(obs)) {
    useAlert(t('ORDERS.DETAIL.OBSERVATIONS_ALPHANUMERIC'));
    return;
  }

  try {
    isSubmitting.value = true;

    // Step 1: Try to create the order in Aquasis
    let aquasisOrderId = null;
    let exploitationCode = '';
    let aquasisWarning = false;

    let aquasisErrorDetail = '';

    const contractNum = contractNumber.value?.trim();
    if (!contractNum) {
      useAlert(t('TICKETS.GENERATE_ORDER.CONTRACT_REQUIRED'));
      isSubmitting.value = false;
      return;
    }
    if (!/^\d+$/.test(contractNum)) {
      useAlert(t('TICKETS.GENERATE_ORDER.CONTRACT_DIGITS_ONLY'));
      isSubmitting.value = false;
      return;
    }
    if (contractValidationStatus.value !== 'valid') {
      useAlert(t('TICKETS.GENERATE_ORDER.CONTRACT_NOT_VALIDATED'));
      isSubmitting.value = false;
      return;
    }

    try {
      const result = await createAquasisOrder();
      if (result) {
        aquasisOrderId = result.aquasisOrderId;
        exploitationCode = result.exploitationCode || '';
      }
    } catch (error) {
      console.error('Error creating Aquasis order:', error);
      if (error.isFieldError) {
        isSubmitting.value = false;
        return;
      }
      aquasisWarning = true;
      aquasisErrorDetail =
        error.aquasisFault ||
        extractSoapFaultString(error.message) ||
        error.message ||
        '';
    }

    // Step 2: Create local service order record
    const orderData = buildLocalServiceOrderPayload(
      aquasisOrderId,
      exploitationCode
    );
    if (aquasisWarning && aquasisErrorDetail) {
      orderData.aquasis_sync_error = aquasisErrorDetail;
    }

    const response = await TicketsAPI.createServiceOrder(
      props.ticket.display_id,
      orderData
    );

    // Step 3: Link the Aquasis order to the CEA case
    if (aquasisOrderId) {
      await linkAquasisOrder(aquasisOrderId);
    }

    if (aquasisWarning) {
      const detail = aquasisErrorDetail ? `: ${aquasisErrorDetail}` : '';
      useAlert(
        `Orden local creada pero no se pudo registrar en Aquasis${detail}`
      );
    } else if (aquasisOrderId) {
      useAlert(
        `Orden de servicio generada exitosamente (Aquasis: ${aquasisOrderId})`
      );
    } else {
      useAlert('Orden de servicio generada exitosamente');
    }

    emit('generated', response.data);
    handleClose();
  } catch (error) {
    console.error('Error creating service order:', error);
    useAlert('Error al generar la orden de servicio');
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = () => {
  if (mode.value === 'link') {
    if (showExternalAquasisForm.value) {
      void handleLinkExternalAquasisSubmit();
    } else {
      void handleLinkTicketSubmit();
    }
  } else {
    void handleCreateSubmit();
  }
};

const detectOrderClass = () => {
  const subcategoryCode = props.ticket.ticket_subcategory?.code || '';
  if (subcategoryCode.startsWith('SRV-')) {
    return 'service_order';
  }
  return 'work_order';
};

const initModal = async () => {
  if (contractValidateTimer) clearTimeout(contractValidateTimer);
  contractValidateTimer = null;
  contractValidationStatus.value = 'idle';
  contractOwnerLabel.value = '';
  contractNumber.value = props.ticket.contract_number || '';
  const detected = detectOrderClass();
  detectedOrderClass.value = detected;
  selectedOrderClass.value = detected;
  const rawDesc = (props.ticket.description || '').replace(/\s+/g, ' ').trim();
  observations.value = sanitizeObservations(rawDesc).slice(0, 80);

  await fetchLookups();
  await fetchSubcategoryDefaults();
  await nextTick();
  if (contractValidateTimer) clearTimeout(contractValidateTimer);
  contractValidateTimer = null;
  await runContractValidation();
};

onMounted(() => {
  if (props.show) {
    dialogRef.value?.open();
    initModal();
  }
});

watch(selectedOrderTypeId, () => {
  if (isApplyingSubcategoryDefaults.value) return;
  selectedOrderMotiveId.value = '';
  selectedRepairCodeId.value = '';
  const motives = effectiveOrderMotives.value;
  if (motives.length === 1) {
    selectedOrderMotiveId.value = motives[0].id;
  }
});

watch(
  effectiveOrderMotives,
  motives => {
    if (!selectedOrderMotiveId.value) {
      if (motives.length === 1) {
        selectedOrderMotiveId.value = motives[0].id;
      }
      return;
    }
    const stillValid = motives.some(
      m => String(m.id) === String(selectedOrderMotiveId.value)
    );
    if (!stillValid) {
      selectedOrderMotiveId.value = '';
      if (motives.length === 1) {
        selectedOrderMotiveId.value = motives[0].id;
      }
    }
  },
  { flush: 'post' }
);

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      dialogRef.value?.open();
      initModal();
    } else {
      dialogRef.value?.close();
    }
  }
);

watch(contractNumber, () => {
  scheduleContractValidation();
});


defineExpose({ dialogRef });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialogRef"
    width="md"
    :title="dialogTitle"
    overflow-y-auto
    :is-loading="isSubmitting"
    :confirm-button-label="confirmButtonLabel"
    :disable-confirm-button="!isFormValid || isSubmitting"
    @close="handleClose"
    @confirm="handleSubmit"
  >
    <div class="flex flex-col gap-4">
      <!-- Ticket Info -->
      <div class="bg-n-solid-3 rounded-lg p-3 border border-n-weak">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-n-slate-11">Ticket</span>
          <span class="font-mono text-sm font-semibold text-n-brand">{{
            ticket.folio
          }}</span>
        </div>
        <p class="text-sm text-n-slate-12">{{ ticket.title }}</p>
      </div>

      <!-- Mode Toggle -->
      <div class="flex gap-2">
        <button
          class="flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors"
          :class="
            mode === 'create'
              ? 'border-n-brand bg-n-brand/5 text-n-brand'
              : 'border-n-weak bg-n-solid-3 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="mode = 'create'"
        >
          {{ $t('TICKETS.GENERATE_ORDER.MODE_CREATE') }}
        </button>
        <button
          class="flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors"
          :class="
            mode === 'link'
              ? 'border-n-brand bg-n-brand/5 text-n-brand'
              : 'border-n-weak bg-n-solid-3 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="mode = 'link'"
        >
          {{ $t('TICKETS.GENERATE_ORDER.MODE_LINK') }}
        </button>
      </div>

      <!-- LINK MODE -->
      <template v-if="mode === 'link'">
        <div class="flex flex-col gap-3">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('TICKETS.GENERATE_ORDER.SEARCH_PLACEHOLDER')"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
            @input="onSearchInput"
          />

          <!-- Loading -->
          <div
            v-if="isSearching"
            class="flex items-center justify-center gap-2 py-4"
          >
            <Spinner :size="20" />
            <span
              v-if="queryLooksLikeAquasisId"
              class="text-xs text-n-slate-10"
            >
              Consultando Aquasis...
            </span>
          </div>

          <!-- No results -->
          <p
            v-else-if="
              searchQuery.trim().length >= 2 && searchResults.length === 0
            "
            class="text-sm text-n-slate-10 text-center py-4"
          >
            {{ $t('TICKETS.GENERATE_ORDER.NO_RESULTS') }}
          </p>

          <!-- Search results -->
          <div
            v-else-if="searchResults.length > 0"
            class="flex flex-col gap-2 max-h-64 overflow-y-auto"
          >
            <div
              v-for="order in searchResults"
              :key="order.id"
              class="p-3 rounded-lg border cursor-pointer transition-colors"
              :class="
                selectedOrderId === order.id
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-solid-3 hover:bg-n-alpha-2'
              "
              @click="selectOrder(order.id)"
            >
              <div class="flex items-center justify-between mb-1">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-mono text-n-slate-11">
                    #{{ order.id }}
                  </span>
                  <span
                    v-if="order.aquasis_order_id"
                    class="px-1.5 py-0.5 text-xs rounded bg-n-brand/10 text-n-blue-text font-mono"
                  >
                    {{ order.aquasis_order_id }}
                  </span>
                </div>
                <span
                  class="px-2 py-0.5 text-xs rounded-full"
                  :class="getOrderStatusClass(order.status)"
                >
                  {{ order.status }}
                </span>
              </div>
              <p
                v-if="order.order_type"
                class="text-sm text-n-slate-12 font-medium"
              >
                {{ order.order_type }}
              </p>
              <p v-if="order.ticket" class="text-xs text-n-slate-11 mt-1">
                Ticket: {{ order.ticket.folio }}
              </p>
            </div>
          </div>

          <!-- Auto-detected: Aquasis-style ID with no AGORA match → show catalog fields inline -->
          <div v-if="showExternalAquasisForm" class="flex flex-col gap-3">
            <!-- status: found -->
            <div
              v-if="aquasisLookupStatus === 'found'"
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-teal-9/10 border border-n-teal-9/30"
            >
              <svg
                class="size-4 shrink-0 text-n-teal-11"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              <span class="text-xs text-n-teal-11">
                Orden
                <span class="font-mono font-semibold">{{
                  searchQuery.trim()
                }}</span>
                encontrada en Aquasis. Datos pre-llenados — revisa y confirma.
              </span>
            </div>

            <!-- status: not-found -->
            <div
              v-else-if="aquasisLookupStatus === 'not-found'"
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-amber-9/10 border border-n-amber-9/30"
            >
              <svg
                class="size-4 shrink-0 text-n-amber-11"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path
                  d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                />
                <path d="M12 9v4M12 17h.01" />
              </svg>
              <span class="text-xs text-n-amber-11">
                <span class="font-mono font-semibold">{{
                  searchQuery.trim()
                }}</span>
                no encontrada en AGORA ni en Aquasis. Verifica el ID o completa los datos manualmente.
              </span>
            </div>

            <!-- status: needs explotación (refreshData 500 without operationalSiteID) -->
            <div
              v-else-if="aquasisLookupStatus === 'needs-explotacion'"
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-ruby-9/10 border border-n-ruby-9/25"
            >
              <svg
                class="size-4 shrink-0 text-n-ruby-11"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span class="text-xs text-n-ruby-11">
                Para consultar la orden en Aquasis hace falta la
                <strong>explotación</strong> (sitio operativo). Asigna
                <strong>número de contrato</strong> al ticket o asegúrate de que el ticket tenga
                explotación; luego vuelve a buscar.
              </span>
            </div>

            <!-- status: error -->
            <div
              v-else-if="aquasisLookupStatus === 'error'"
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-amber-9/10 border border-n-amber-9/30"
            >
              <svg
                class="size-4 shrink-0 text-n-amber-11"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span class="text-xs text-n-amber-11">
                No se pudo consultar Aquasis para
                <span class="font-mono font-semibold">{{
                  searchQuery.trim()
                }}</span>. Completa los datos manualmente.
              </span>
            </div>

            <!-- status: idle / fallback -->
            <div
              v-else
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-brand/8 border border-n-brand/20"
            >
              <svg
                class="size-4 shrink-0 text-n-blue-text"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
              <span class="text-xs text-n-blue-text">
                Orden
                <span class="font-mono font-semibold">{{
                  searchQuery.trim()
                }}</span>
                no encontrada en AGORA. Completa los datos para registrarla.
              </span>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Clase de Orden <span class="text-n-ruby-11">*</span>
              </label>
              <div class="flex gap-4">
                <label
                  class="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors"
                  :class="
                    selectedOrderClass === 'work_order'
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak bg-n-solid-3'
                  "
                >
                  <input
                    v-model="selectedOrderClass"
                    type="radio"
                    value="work_order"
                    class="accent-woot-500"
                  />
                  <span class="text-sm text-n-slate-12">Orden de Trabajo</span>
                </label>
                <label
                  class="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors"
                  :class="
                    selectedOrderClass === 'service_order'
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak bg-n-solid-3'
                  "
                >
                  <input
                    v-model="selectedOrderClass"
                    type="radio"
                    value="service_order"
                    class="accent-woot-500"
                  />
                  <span class="text-sm text-n-slate-12">Orden de Servicio</span>
                </label>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Tipo de Orden <span class="text-n-ruby-11">*</span>
              </label>
              <select
                v-model="selectedOrderTypeId"
                :disabled="isLoadingLookups || isOrderTypeLocked"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
              >
                <option value="" disabled>
                  {{
                    isLoadingLookups
                      ? 'Cargando tipos...'
                      : 'Seleccione un tipo de orden'
                  }}
                </option>
                <option
                  v-for="ot in effectiveOrderTypes"
                  :key="ot.id"
                  :value="ot.id"
                >
                  {{ ot.code }} - {{ ot.description }}
                </option>
              </select>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Motivo CEA <span class="text-n-ruby-11">*</span>
              </label>
              <select
                v-model="selectedOrderMotiveId"
                :disabled="isLoadingLookups || isOrderMotiveLocked"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
              >
                <option value="" disabled>
                  {{
                    isLoadingLookups
                      ? 'Cargando motivos...'
                      : 'Seleccione un motivo'
                  }}
                </option>
                <option
                  v-for="om in effectiveOrderMotives"
                  :key="om.id"
                  :value="om.id"
                >
                  {{ om.code }} - {{ om.description }}
                </option>
              </select>
            </div>

            <div
              v-if="
                selectedOrderType?.code === '32' &&
                subcategoryRepairCodes.length > 0
              "
              class="flex flex-col gap-2"
            >
              <label class="text-sm font-medium text-n-slate-12">
                Codigo de Reparacion
                <span v-if="isRepairCodeRequired" class="text-n-ruby-11"
                  >*</span
                >
              </label>
              <select
                v-model="selectedRepairCodeId"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              >
                <option value="">
                  {{
                    isRepairCodeRequired
                      ? 'Seleccione un codigo (obligatorio)'
                      : 'Seleccione un codigo (opcional)'
                  }}
                </option>
                <option
                  v-for="rc in subcategoryRepairCodes"
                  :key="rc.id"
                  :value="rc.id"
                >
                  {{ rc.code }} - {{ rc.description }}
                </option>
              </select>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Fecha de Solicitud <span class="text-n-ruby-11">*</span>
              </label>
              <input
                v-model="requestDate"
                type="date"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Descripcion
              </label>
              <input
                v-model="description"
                type="text"
                placeholder="Descripcion breve del motivo de la orden..."
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
              />
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Observaciones
                <span class="text-n-ruby-11">*</span>
              </label>
              <textarea
                :value="observations"
                rows="4"
                maxlength="80"
                placeholder="Ingrese observaciones adicionales (solo letras, números y puntuación básica)"
                class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
                @input="handleObservationsInput"
              />
              <span class="text-xs text-n-slate-10 text-right block">
                {{ observations.length }}/80
              </span>
            </div>
          </div>
        </div>
      </template>

      <!-- CREATE MODE (existing form) -->
      <template v-else>
        <!-- Order Class -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Clase de Orden <span class="text-n-ruby-11">*</span>
          </label>
          <div class="flex gap-4">
            <label
              class="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors"
              :class="
                selectedOrderClass === 'work_order'
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-solid-3'
              "
            >
              <input
                v-model="selectedOrderClass"
                type="radio"
                value="work_order"
                class="accent-woot-500"
              />
              <span class="text-sm text-n-slate-12">Orden de Trabajo</span>
            </label>
            <label
              class="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors"
              :class="
                selectedOrderClass === 'service_order'
                  ? 'border-n-brand bg-n-brand/5'
                  : 'border-n-weak bg-n-solid-3'
              "
            >
              <input
                v-model="selectedOrderClass"
                type="radio"
                value="service_order"
                class="accent-woot-500"
              />
              <span class="text-sm text-n-slate-12">Orden de Servicio</span>
            </label>
          </div>
          <p
            v-if="detectedOrderClass === selectedOrderClass"
            class="text-xs text-n-slate-10"
          >
            Detectado automaticamente por la subcategoria del ticket
          </p>
          <p v-else class="text-xs text-n-amber-11">
            Clase modificada manualmente (detectada:
            {{
              detectedOrderClass === 'service_order'
                ? 'Orden de Servicio'
                : 'Orden de Trabajo'
            }})
          </p>
        </div>

        <!-- Contract Number -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('TICKETS.GENERATE_ORDER.CONTRACT_LABEL') }}
            <span class="text-n-ruby-11">*</span>
          </label>
          <div class="grid grid-cols-1">
            <input
              v-model="contractNumber"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              autocomplete="off"
              class="col-start-1 row-start-1 min-h-0 min-w-0"
              :placeholder="$t('TICKETS.GENERATE_ORDER.CONTRACT_PLACEHOLDER')"
              :class="contractInputClass"
            />
            <div
              v-if="showContractValidationAffix"
              class="col-start-1 row-start-1 z-10 flex items-center justify-end pr-3 pointer-events-none"
            >
              <Spinner
                v-if="contractValidationStatus === 'checking'"
                :size="18"
              />
              <svg
                v-else-if="contractValidationStatus === 'valid'"
                class="size-5 shrink-0 -translate-y-1 text-n-teal-11"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              <svg
                v-else-if="
                  contractValidationStatus === 'invalid' ||
                  contractValidationStatus === 'error'
                "
                class="size-5 shrink-0 -translate-y-1 text-n-ruby-11"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </div>
          </div>
          <p
            v-if="contractOwnerLabel && contractValidationStatus === 'valid'"
            class="text-xs text-n-slate-11"
          >
            {{
              $t('TICKETS.GENERATE_ORDER.CONTRACT_OWNER', {
                name: contractOwnerLabel,
              })
            }}
          </p>
          <p
            v-else-if="contractValidationStatus === 'invalid'"
            class="text-xs text-n-ruby-11"
          >
            {{ contractInvalidMessage }}
          </p>
          <p
            v-else-if="contractValidationStatus === 'error'"
            class="text-xs text-n-ruby-11"
          >
            {{ $t('TICKETS.GENERATE_ORDER.CONTRACT_VALIDATION_ERROR') }}
          </p>
          <p v-if="!ticket.contract_number" class="text-xs text-n-amber-11">
            {{ $t('TICKETS.GENERATE_ORDER.CONTRACT_NO_TICKET') }}
          </p>
        </div>

        <!-- Manual idPtoServicio (shown when auto-lookup failed) -->
        <div
          v-if="showManualFields"
          class="p-3 rounded-lg bg-n-amber-3 border border-n-amber-6"
        >
          <p class="text-xs text-n-amber-11 mb-2">
            {{ aquasisLookupError }}
          </p>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              Punto de Servicio (idPtoServicio)
            </label>
            <input
              v-model="manualIdPtoServicio"
              type="text"
              placeholder="Ingrese el ID del punto de servicio"
              class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
            />
          </div>
        </div>

        <!-- Order Type (CEA lookup) -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Tipo de Orden <span class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="selectedOrderTypeId"
            :disabled="isLoadingLookups || isOrderTypeLocked"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
          >
            <option value="" disabled>
              {{
                isLoadingLookups
                  ? 'Cargando tipos...'
                  : 'Seleccione un tipo de orden'
              }}
            </option>
            <option v-for="ot in effectiveOrderTypes" :key="ot.id" :value="ot.id">
              {{ ot.code }} - {{ ot.description }}
            </option>
          </select>
          <p v-if="hasOtConstraint" class="text-xs text-n-slate-10">
            Sugerido por la subcategoria del ticket
          </p>
        </div>

        <!-- Order Motive (CEA lookup) -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Motivo CEA <span class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="selectedOrderMotiveId"
            :disabled="isLoadingLookups || isOrderMotiveLocked"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand disabled:opacity-50"
          >
            <option value="" disabled>
              {{
                isLoadingLookups
                  ? 'Cargando motivos...'
                  : 'Seleccione un motivo'
              }}
            </option>
            <option
              v-for="om in effectiveOrderMotives"
              :key="om.id"
              :value="om.id"
            >
              {{ om.code }} - {{ om.description }}
            </option>
          </select>
          <p v-if="hasOmConstraint" class="text-xs text-n-slate-10">
            Opciones filtradas por la subcategoria del ticket
          </p>
        </div>

        <!-- Repair Code (solo tipo 32 — orden de reparación) -->
        <div
          v-if="
            selectedOrderType?.code === '32' &&
            subcategoryRepairCodes.length > 0
          "
          class="flex flex-col gap-2"
        >
          <label class="text-sm font-medium text-n-slate-12">
            Codigo de Reparacion
            <span v-if="isRepairCodeRequired" class="text-n-ruby-11">*</span>
          </label>
          <select
            v-model="selectedRepairCodeId"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option value="">
              {{
                isRepairCodeRequired
                  ? 'Seleccione un codigo (obligatorio)'
                  : 'Seleccione un codigo (opcional)'
              }}
            </option>
            <option
              v-for="rc in subcategoryRepairCodes"
              :key="rc.id"
              :value="rc.id"
            >
              {{ rc.code }} - {{ rc.description }}
            </option>
          </select>
        </div>

        <!-- Request Date -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Fecha de Solicitud <span class="text-n-ruby-11">*</span>
          </label>
          <input
            v-model="requestDate"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>

        <!-- Description (formerly "Motivo" free text, renamed to avoid confusion) -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Descripcion
          </label>
          <input
            v-model="description"
            type="text"
            placeholder="Descripcion breve del motivo de la orden..."
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand"
          />
        </div>

        <!-- Observations -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Observaciones
            <span class="text-n-ruby-11">*</span>
          </label>
          <textarea
            :value="observations"
            rows="4"
            maxlength="80"
            placeholder="Ingrese observaciones adicionales (solo letras, números y puntuación básica)"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-2 focus:ring-n-brand resize-none"
            @input="handleObservationsInput"
          />
          <span class="text-xs text-n-slate-10 text-right block">
            {{ observations.length }}/80
          </span>
        </div>

        <div class="text-xs text-n-slate-10 mt-2">
          <span class="text-n-ruby-11">*</span> Campos requeridos
        </div>
      </template>
    </div>
  </Dialog>
</template>
